import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { MovingBorderWrapper } from "@/components/ui/moving-border";
import { SEO, seoConfig } from "@/hooks/useSEO";
import { generateWebPageSchema, generateBreadcrumbSchema } from "@/lib/jsonld";

// Images
import heroImage from "@/assets/usecases/conferenciers/hero.webp";
import feature1Image from "@/assets/usecases/conferenciers/feature-1.webp";
import feature2Image from "@/assets/usecases/conferenciers/feature-2.webp";
import feature3Image from "@/assets/usecases/conferenciers/feature-3.webp";

const features = [
  {
    number: "1",
    title: "Récupérez les contacts directement depuis la scène",
    text: "Affichez un QR code sur votre dernière slide. Proposez votre présentation en téléchargement, une ressource gratuite, ou une réduction exclusive accessible uniquement via la carte Wallet. Regardez votre audience sortir son téléphone et s'abonner instantanément à votre canal.",
    image: feature1Image,
    imagePosition: "left" as const,
  },
  {
    number: "2",
    title: "Envoyez des mises à jour impossibles à rater",
    text: "La session change de salle ? Le keynote commence dans 15 minutes ? Envoyez une notification qui s'affiche sur l'écran verrouillé de tous les participants. Fini la confusion, votre événement se déroule sans accroc. Sans compter sur des emails que personne n'ouvrira.",
    image: feature2Image,
    imagePosition: "right" as const,
  },
  {
    number: "3",
    title: "Gardez le contact après l'événement",
    text: "Votre lien avec les participants ne s'arrête pas à la fin de l'événement. Le lendemain, envoyez un message de remerciement, un lien vers votre formulaire de feedback, ou une offre exclusive pour prolonger la relation. Votre nouvelle liste d'abonnés est là, prête à vous écouter.",
    image: feature3Image,
    imagePosition: "left" as const,
  },
];

const badges = [
  "Vos prochaines interventions",
  "Vos nouvelles formations",
  "Vos offres réservées",
];

export default function UseCasesConferenciers() {
  const jsonLdSchemas = [
    generateWebPageSchema("Conférenciers & Événements", seoConfig.useCasesConferenciers.description, "/cas-utilisation/conferenciers-evenements"),
    generateBreadcrumbSchema([
      { name: "Accueil", path: "/" },
      { name: "Cas d'utilisation", path: "/#a-qui-sadresse" },
      { name: "Conférenciers & Événements", path: "/cas-utilisation/conferenciers-evenements" }
    ])
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO {...seoConfig.useCasesConferenciers} jsonLd={jsonLdSchemas} />
      <Header />

      {/* SECTION 1 - HERO */}
      <section className="relative pt-24 lg:pt-40 pb-[37.5px] sm:pb-[47.5px] lg:pb-[65px] overflow-hidden">
        <div className="container mx-auto px-6 lg:px-12 xl:px-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 xl:gap-16 items-center">
            {/* Left: Text content */}
            <div className="max-w-2xl text-center lg:text-left mx-auto lg:mx-0">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-foreground mb-6">
                Votre conférence d'1h = <span className="text-[#0c97a9]">une audience pour toujours</span>
              </h1>

              <p className="leading-relaxed mb-8 font-medium text-[#4f5b5d] text-base sm:text-lg">
                Affichez un QR code sur votre présentation. Vos participants ajoutent votre carte à leur Wallet en un geste. Vous gardez le contact pour annoncer vos prochaines dates, partager vos ressources ou gérer les imprévus en temps réel.
              </p>

              <div className="flex flex-col items-center lg:items-start">
                <MovingBorderWrapper duration={3000} borderRadius="0.5rem">
                  <Button asChild size="lg" className="bg-primary hover:bg-[hsl(187,87%,32%)] text-primary-foreground shadow-lg hover:shadow-xl transition-all duration-300 text-base px-8 py-6">
                    <a href="https://app.unotif.com/auth?mode=signup">
                      Créez votre première carte
                    </a>
                  </Button>
                </MovingBorderWrapper>
                <p className="text-sm text-muted-foreground mt-2">
                  👉 Sans engagement, sans carte bancaire
                </p>
              </div>
            </div>

            {/* Right: Hero Image */}
            <div className="hidden lg:flex justify-center lg:justify-end items-center">
              <img
                src={heroImage}
                alt="Conférenciers et événements avec Unotif"
                loading="lazy"
                className="rounded-2xl max-w-full h-auto"
                style={{
                  boxShadow: "0 8px 32px -8px rgba(0,0,0,0.12), 0 4px 16px -4px rgba(0,0,0,0.08)",
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2 - Votre outil pour des événements sans accroc */}
      <section className="bg-[#FAFBFB] pt-[37.5px] sm:pt-[47.5px] lg:pt-[65px] pb-[37.5px] sm:pb-[47.5px] lg:pb-[65px]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#074155] mb-6">
              Votre outil pour <span className="text-[#0c97a9]">des événements sans accroc</span>
            </h2>
            <p className="text-base sm:text-lg font-medium leading-relaxed text-[#4f5b5d]">
              De la capture de contacts à la communication en temps réel, <span className="font-semibold text-[#0c97a9]">Unotif</span> est l'outil indispensable pour tous vos événements.
            </p>
          </div>

          {/* Features */}
          <div className="space-y-12 sm:space-y-16 lg:space-y-20">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`grid lg:grid-cols-2 gap-8 lg:gap-12 items-center ${
                  feature.imagePosition === "right" ? "lg:grid-flow-dense" : ""
                }`}
              >
                {/* Image */}
                <div className={feature.imagePosition === "right" ? "lg:col-start-2" : ""}>
                  <img
                    src={feature.image}
                    alt={feature.title}
                    loading="lazy"
                    className="rounded-2xl w-full h-auto"
                    style={{
                      boxShadow: "0 8px 32px -8px rgba(0,0,0,0.12), 0 4px 16px -4px rgba(0,0,0,0.08)",
                    }}
                  />
                </div>

                {/* Text */}
                <div className={feature.imagePosition === "right" ? "lg:col-start-1 lg:row-start-1" : ""}>
                  <div className="flex items-start gap-4">
                    <span className="text-4xl sm:text-5xl font-bold text-[#0c97a9]">
                      {feature.number}.
                    </span>
                    <div>
                      <h3 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4">
                        {feature.title}
                      </h3>
                      <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
                        {feature.text}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Result text */}
          <div className="text-center mt-12 sm:mt-16 lg:mt-20 max-w-3xl mx-auto">
            <p className="text-base sm:text-lg font-semibold leading-relaxed text-[#074155]">
              Résultat : un canal direct avec tous vos participants, pendant et bien après l'événement.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 3 - Ne perdez plus jamais votre audience */}
      <section className="pt-[37.5px] sm:pt-[47.5px] lg:pt-[65px] pb-16 sm:pb-20 lg:pb-24">
        <div className="container mx-auto px-6 lg:px-12 xl:px-20">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#074155] mb-6">
              Ne perdez plus jamais votre audience.
            </h2>
            <p className="text-base sm:text-lg font-medium leading-relaxed text-[#4f5b5d] mb-4">
              Vous avez le choix. Laisser vos participants repartir sans garder de lien réel avec eux.
            </p>
            <p className="text-base sm:text-lg font-medium leading-relaxed text-[#4f5b5d] mb-10">
              Ou capter leur attention pendant qu'elle est à son maximum et transformer chaque intervention en canal de communication durable.
            </p>

            {/* Badges */}
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-6">
              {badges.map((badge, index) => (
                <span
                  key={index}
                  className="rounded-full border border-[#074155] bg-white px-4 py-2 text-sm sm:text-base font-medium text-[#074155]"
                >
                  {badge}
                </span>
              ))}
            </div>

            {/* Callout */}
            <p className="text-base sm:text-lg font-medium text-[#074155]">
              👀 Assurez-vous qu'ils le voient.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 4 - CTA Final */}
      <section className="pb-16 sm:pb-20 lg:pb-24">
        <div className="container mx-auto px-6 lg:px-12 xl:px-20">
          <Card className="max-w-2xl mx-auto text-center p-8 sm:p-10 lg:p-12 border border-gray-200 shadow-lg">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#074155] mb-4">
              Votre meilleur événement commence maintenant !
            </h2>
            <p className="text-base sm:text-lg font-medium text-[#4f5b5d] mb-8">
              Découvrez la puissance d'un canal qui vous appartient, gratuitement.
            </p>
            <div className="flex flex-col items-center">
              <Button asChild size="lg" className="bg-primary hover:bg-[hsl(187,87%,32%)] text-primary-foreground shadow-lg hover:shadow-xl transition-all duration-300 text-base px-8 py-6">
                <a href="https://app.unotif.com/auth?mode=signup">
                  Commencer maintenant
                </a>
              </Button>
              <p className="text-sm text-muted-foreground mt-3">
                Sans carte bancaire
              </p>
            </div>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
}
